import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from "reactstrap";
import Select from 'react-select';
import AsyncSelect from "react-select/async";
import { toast } from 'react-toastify';
import { PostData } from '../../../../services/FetchData';
import { ProductContext } from '../../../../store';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import './DynamicBundling.scss';
import ReactPaginate from "react-paginate";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faFilter } from '@fortawesome/free-solid-svg-icons';
import debounce from 'lodash.debounce';

const DynamicBundling = ()  => {
	const [isModalOpen, setIsModalOpen] = useState(false),
		[selectedProduct, setSelectedProduct] = useState(null),
		[showDetailPage, setShowDetailPage] = useState(false),
		[editProduct, setEditProduct] = useState(null),
		[removedProducts, setRemovedProducts] = useState([]),
		[productBundleAddModal, setProductBundleAddModal] = useState(false),
		{ currentWebsiteId } = useContext(ProductContext),
		[products, setProducts] = useState([]),
		[bundleLoader, setBundleLoader] = useState(true),
		[searchQuery, setSearchQuery] = useState(''),
		[productSearchQuery, setProductSearchQuery] = useState(''),
		[productSearchResult, setProductSearchResult] = useState([]),
		[productSearchLoader, setProductSearchLoader]	= useState(false),
		[productBundleSelect, setProductBundleSelect]	= useState(null),
		[showFilterSlider, setShowFilterSlider] = useState(false),
		[selectedCategories, setSelectedCategories] = useState([]),
		[isFiltered, setIsFiltered] = useState(false),
		[filterLoader, setFilterLoader] = useState(false),
		[searchResults, setSearchResults] = useState([]),
		[searchLoader, setSearchLoader] = useState(false),
		[page, setPage]	= useState(1),
		[limit, setLimit]	= useState(15),
		[totalPages, setTotalPages]	= useState(1),
		[bundleCreationLoader, setBundleCreationLoader] = useState(false),
		[editingProductId, setEditingProductId] = useState(null),
		[bundleProducts, setBundleProducts] = useState([]),
		[addProductIndex, setAddProductIndex]	= useState(null),
		[saveBundleLoader, setSaveBundleLoader] = useState(false),
		timeoutId	= useRef();

	useEffect(() => {
		if( currentWebsiteId ){
			getBundleProducts();
		}
	}, [page, limit, currentWebsiteId])


	useEffect(() => {
		if( currentWebsiteId ){
			timeoutId.current = setTimeout(() => {
				if (productSearchQuery) {
					searchBundleProducts(productSearchQuery);
				}
			}, 500);
		}
		return () => clearTimeout(timeoutId.current);
	}, [productSearchQuery, currentWebsiteId])

	const getBundleProducts = async () => {
		const postData = { 
			websiteId: currentWebsiteId, 
			limit, 
			page
		};

		// Add filter parameters if any filters are applied
		if (searchQuery.trim() || selectedCategories.length > 0) {
			postData.filters = {
				search: searchQuery.trim(),
				categories: selectedCategories.map(cat => cat.value)
			};
		}

		setBundleLoader(true);
		try {
			const response = await PostData("ms3", "api/v1/admin/bundle/getAllBundleProductData", postData);
			
			if (response !== "Invalid" && response !== undefined) {
				if (response.status === "success") {
					let productData	= response.data.productData
					let totalRecords	= response.data.total
					setProducts(productData);
					setTotalPages(Math.ceil( totalRecords / limit ))
				} else if (response.status === "failure") {
					//toast.error(response.message);
				} else {
					//toast.error(response.message?.message || 'Failed to fetch bundle products');
				}
			}
		} catch (error) {
			console.error("Error fetching bundle products:", error);
			//toast.error("Failed to fetch bundle products");
		} finally {
			setBundleLoader(false);
		}
	}

	const searchProducts = async (query) => {
		if (!query.trim()) {
			setSearchResults([]);
			return;
		}

		setSearchLoader(true);
		try {
			const postData = {
				websiteId: currentWebsiteId,
				searchQuery: query.trim()
			};

			const response = await PostData("ms3", "api/v1/admin/bundle/searchProductsWithBundleInfo", postData);

			if (response !== "Invalid" && response !== undefined) {
				if (response.status === "success") {
					let products = response.data.missingInElastic;
					setSearchResults(products);
				} else if (response.status === "failure") {
					toast.error(response.message);
					setSearchResults([]);
				} else {
					toast.error(response.message?.message || 'Search failed');
					setSearchResults([]);
				}
			}
		} catch (error) {
			console.error("Error searching products:", error);
			toast.error("Failed to search products");
			setSearchResults([]);
		} finally {
			setSearchLoader(false);
		}
	}

	const searchBundleProducts = async (query) => {
		if (!query.trim()) {
			setProductSearchResult([]);
			return;
		}
		setProductSearchLoader(true);
		try {
			let productIds	= bundleProducts.filter((product) => product.id).map(product => product.id);
			productIds.push(editProduct.id)
			const postData = {
				websiteId: currentWebsiteId,
				searchQuery: query.trim(),
				productIds
			}
			const response = await PostData("ms3", "api/v1/admin/bundle/searchProductEditBundle", postData);
			if (response !== "Invalid" && response !== undefined) {
				if (response.status === "success") {
					let products = response.data.products;
					setProductSearchResult(products);
				} else if (response.status === "failure") {
					//toast.error(response.message);
					setProductSearchResult([]);
				} else {
					//toast.error(response.message?.message || 'Search failed');
					setProductSearchResult([]);
				}
			}
		} catch (error) {
			console.error("Error searching products:", error);
			//toast.error("Failed to search products");
			setProductSearchResult([]);
		} finally {
			setProductSearchLoader(false);
		}
	}

	const getCurrentActiveProductCount = () => {
		return bundleProducts.filter(product => product.id).length || 0;
	}

	const getCategoryNames = async (inputValue) => {
		const requestBody = { 
			websiteId: currentWebsiteId,
			categoryName: inputValue
		};
		console.log('Request body:', requestBody);
		
		try {
			const response = await PostData("ms3", "api/v1/admin/bundle/getCategoryNames", requestBody);
			
			if (response !== "Invalid" && response !== undefined) {
				if (response.status === "success") {
					// The API already returns data in the correct format for react-select
					const results = response.data?.searchResult || [];
					return results;
				} else {
					console.error("Error fetching categories:", response.message);
					return [];
				}
			}
			return [];
		} catch (error) {
			console.error("Error fetching categories:", error);
			return [];
		}
	}
 
	const debouncedCategoryQuery = useCallback(
		debounce((inputValue, callback) => {
			getCategoryNames(inputValue).then(callback);
		}, 500),
		[currentWebsiteId]
	);

	const getBundleByProductId = async (productId) => {
		setEditingProductId(productId);
		try {
			const postData = {
				websiteId: currentWebsiteId,
				productId: productId
			}
			const response = await PostData("ms3", "api/v1/admin/bundle/getBundleByProductId", postData);
			if (response !== "Invalid" && response !== undefined) {
				if (response.status === "success") {
					setEditProduct(response.data.bundle.product);
					setBundleProducts(response.data.bundle.products)
					setShowDetailPage(true)
				} else if (response.status === "failure") {
					toast.error(response.message || 'Failed to fetch bundle details');
				} else {
					toast.error(response.message?.message || 'Failed to fetch bundle details');
				}
			} else {
				toast.error('Invalid response from server');
			}
		} catch (error) {
			console.error("Error fetching bundle details:", error);
			toast.error("Failed to fetch bundle details");
		}
	}

	const handleAddProduct = (product) => {
		if (product) {
			setSelectedProduct(product);
			setSearchQuery('');
			setSearchResults([]);
		}
	}

	const toggleModal = () => {
		setIsModalOpen(!isModalOpen);
		if (isModalOpen) {
			setSearchQuery('');
			setSearchResults([]);
			setProductBundleSelect(null);
		}
	}

	const toggleFilterSlider = () => {
		setShowFilterSlider(!showFilterSlider);
	}

	const removeProduct = () => {
		setSelectedProduct(null);
	}

	const removeProductSelectBundle = () => {
		setProductBundleSelect(null)
	}

	const saveBundle = async () => {
		if ( !selectedProduct ) {
			toast.error('Please select at least one product');
			return;
		}

		setBundleCreationLoader(true);
		try {
			const postData = { websiteId: currentWebsiteId, productId: selectedProduct.id}
			const response = await PostData("ms3", "api/v1/admin/bundle/generateProductBundle", postData);

			if (response !== "Invalid" && response !== undefined) {
				if (response.status === "success") {
					console.log(`Product ${selectedProduct.name} added to bundle successfully`);
				} else if (response.status === "failure") {
					toast.error(`Failed to add ${selectedProduct.name}: ${response.message}`);
				} else {
					toast.error(`Failed to add ${selectedProduct.name}: ${response.message?.message || 'Unknown error'}`);
				}
			}
			setIsModalOpen(false);
			setSelectedProduct(null);
			getBundleProducts();

		} catch (error) {
			console.error('Error creating bundle:', error);
			toast.error('Failed to create bundle. Please try again.');
		} finally {
			setBundleCreationLoader(false);
		}
	}

	const closeDetailPage = () => {
		setShowDetailPage(false);
		setEditProduct(null);
		setEditingProductId(null)
		setSaveBundleLoader(false)
		setBundleProducts([]);
	}

	const removeProductFromBundle = (index) => {
		let bundleProductsLocal	= [...bundleProducts]
		bundleProductsLocal[index]	= {}
		setBundleProducts(bundleProductsLocal)
	}

	const openAddProductModal = (index) => {
		setAddProductIndex(index)
		setProductBundleAddModal(true);
	}

	const closeAddProductModal = () => {
		setProductBundleAddModal(false);
		setProductBundleSelect(null);
		setAddProductIndex(null)
	}

	const handleBundleProductSelect = (product) => {
		if (product) {
			setProductBundleSelect(product);
			setProductSearchQuery('');
			setProductSearchResult([]);
		}
	}

	const addProductToBundle = () => {
		let bundleProductsLocal	= [...bundleProducts]
		bundleProductsLocal[addProductIndex]	= productBundleSelect
		setBundleProducts(bundleProductsLocal)
		setProductBundleAddModal(false);
		setProductBundleSelect(null);
		setAddProductIndex(null)
	}

	const saveBundleChanges = async () => {
		setSaveBundleLoader(true)
		let selectedProductIds	= bundleProducts.filter(product => product.id).map(product => product.id);
		PostData("ms3", "api/v1/admin/bundle/editBundleProducts", { websiteId: currentWebsiteId, productId: editingProductId, selectedProductIds}).then(response => {
			if( response !== "Invalid" ){
				if( response.status === "success" ){
					toast.success(response.message)
				}else if( response.status === "failure" ){
					toast.error(response.message)
				}else{
					toast.error(response.message.message)
				}
			}
			setSaveBundleLoader(false)
			setShowDetailPage(false)
			setEditProduct(null);
			setEditingProductId(null)
			setBundleProducts([]);
		})
	}

	return (
		<React.Fragment>
			<div>
				<Row className="dynamic-bundling-header">
					<Col xs="auto">
						<h2 className="dynamic-bundling-title">Dynamic Bundling</h2>
					</Col>
				</Row>

				<div className="bundle-performance-container">
					<div className="row">
						<div className="analyticsStack col-12">
							<div className="bundle-performance-header">
								
								<div className="header-left"></div>
								<div className="header-actions">
									<Button
										className="cta autoWidth outline me-2"
										onClick={toggleFilterSlider}
										title="Filter products"
										style={{ color: 'black', borderColor: 'black' }}
									>
										<FontAwesomeIcon icon={faFilter} className="me-2" style={{ color: 'black' }} />
										Filter
									</Button>
									<Button
										className="cta autoWidth"
										onClick={toggleModal}
									>
										<i className="fa fa-plus"></i>
										Add Product
									</Button>
								</div>
							</div>

							{bundleLoader ? (
								<div className="bundle-skeleton">
									<SkeletonTheme baseColor="#f7fafc" highlightColor="#eff1ff">
										<table className="analytics-table">
											<thead>
												<tr>
													<th><Skeleton height={20} /></th>
													<th><Skeleton height={20} /></th>
												</tr>
											</thead>
											<tbody>
												{[1, 2, 3, 4, 5].map((item) => (
													<tr key={item} className="analytics-table-row">
														<td>
															<div className="product-info-container" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
																<Skeleton width={60} height={60} style={{ borderRadius: '8px' }} />
																<div className="product-details" style={{ flex: 1 }}>
																	<Skeleton height={16} width="80%" style={{ marginBottom: '4px' }} />
																	<Skeleton height={14} width="40%" />
																</div>
															</div>
														</td>
														<td>
															<Skeleton height={32} width={80} />
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</SkeletonTheme>
								</div>
							) : products.length > 0 ? (
								<React.Fragment>
									<table className="analytics-table">
										<thead>
											<tr>
												<th>Product</th>
												<th>Actions</th>
											</tr>
										</thead>
										<tbody>
											{products.map((bundle) => (
												<tr className="analytics-table-row" key={bundle.id}>
													<td>
														<div className="product-info-container" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
															<img
																src={bundle.productDetails?.image || ''}
																alt={bundle.productDetails?.name || 'Product'}
																style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
															/>
															<div className="product-details">
																<div className="product-name" style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
																	{bundle.productDetails?.name || 'Product Name Not Available'}
																</div>
																<div className="price-amount" style={{ color: '#666', fontSize: '13px' }}>
																	${bundle.productDetails?.price || '0'}
																</div>
															</div>
														</div>
													</td>
													<td>
														<div className="action-buttons">
															<Button
																type="button"
																className="edit-btn me-2"
																onClick={(e) => { e.preventDefault(); getBundleByProductId(bundle.productDetails?.id); }}
																title="Edit Product"
																disabled={editingProductId}
															>
																{editingProductId === bundle.productDetails?.id ? (
																	<>
																		<Spinner size="sm" />
																		<span style={{ marginLeft: '4px' }}>Loading...</span>
																	</>
																) : (
																	<>
																		<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 32 32">
																			<path d="M30.276 1.722C29.168.612 27.69 0 26.12 0s-3.044.61-4.153 1.72L4.294 19.29c-.105.105-.185.23-.235.368l-4 11c-.13.355-.047.756.214 1.03C.466 31.892.73 32 1 32c.098 0 .196-.014.293-.044l9.95-3.052c.155-.047.297-.133.413-.248l18.62-18.62C31.39 8.925 32 7.447 32 5.877c0-1.57-.61-3.046-1.724-4.156zM10.092 27.165L6.368 28.31c-.217-.638-.555-1.202-1.016-1.663-.4-.4-.866-.71-1.356-.96L5.7 21H8v2c0 .553.447 1 1 1h1.765l-.673 3.165zm14.72-14.494L12.628 24.856l.35-1.647c.062-.296-.012-.603-.202-.837-.19-.234-.475-.37-.776-.37h-2v-2c0-.552-.448-1-1-1H7.422L19.315 7.175l.012.01c.732-.732 1.707-1.135 2.742-1.135s2.01.403 2.74 1.136 1.14 1.707 1.14 2.743c0 1.035-.404 2.008-1.138 2.74zm4.05-4.05l-.932.934c-.09-1.43-.683-2.76-1.703-3.782-1.02-1.022-2.354-1.614-3.787-1.703l.938-.932.002-.002C24.11 2.403 25.085 2 26.12 2s2.01.403 2.742 1.136C29.596 3.87 30 4.843 30 5.878c0 1.037-.402 2.01-1.138 2.743zm-6.57-.327l-10 10c-.39.39-.39 1.023 0 1.414.195.195.452.293.708.293s.51-.098.707-.293l10-10c.39-.39.39-1.023 0 1.414-.392-.39-1.023-.39-1.414 0z" fill="currentColor" />
																		</svg>
																		Edit
																	</>
																)}
															</Button>
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
									{totalPages > 1 && (
										<ReactPaginate
											nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
											onPageChange={(event) => setPage(event.selected + 1)}
											pageRangeDisplayed={3}
											marginPagesDisplayed={2}
											pageCount={totalPages}
											previousLabel={<FontAwesomeIcon icon={faChevronLeft} />}
											pageClassName="page-item"
											pageLinkClassName="page-link"
											previousClassName="page-item"
											previousLinkClassName="page-link"
											nextClassName="page-item"
											nextLinkClassName="page-link"
											breakLabel="..."
											breakClassName="page-item"
											breakLinkClassName="page-link"
											containerClassName="pagination BannerPagination mt-5"
											activeClassName="active"
											renderOnZeroPageCount={null}
											forcePage={page - 1}
										/>
									)}
								</React.Fragment>
							) : (
								<div className="no-products-message">
									<p>No bundle products found. Start by creating your first bundle.</p>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Add New Product Modal */}
				<Modal isOpen={isModalOpen} toggle={toggleModal} size="md">
					<ModalHeader toggle={toggleModal}>
						<h4>Add New Product to Bundle</h4>
					</ModalHeader>
					<ModalBody>
						<div className="modal-content">
							<div className="search-row mb-4">
								<div className="search-container">
									<input
										type="text"
										className="form-control"
										placeholder="Search for products..."
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
									/>
								</div>
							</div>

							{searchLoader ? (
								<div className="search-skeleton">
									<h6 className="mb-3">Search Results</h6>
									<SkeletonTheme baseColor="#f7fafc" highlightColor="#eff1ff">
										{[1, 2, 3].map((item) => (
											<div key={item} className="product-item-skeleton" style={{
												display: 'flex',
												alignItems: 'center',
												padding: '15px',
												border: '1px solid #e9ecef',
												borderRadius: '8px',
												marginBottom: '10px'
											}}>
												<Skeleton width={60} height={60} style={{ borderRadius: '6px', marginRight: '15px' }} />
												<div className="product-info" style={{ flex: 1 }}>
													<Skeleton height={16} width="70%" style={{ marginBottom: '4px' }} />
													<Skeleton height={14} width="50%" style={{ marginBottom: '2px' }} />
													<Skeleton height={14} width="30%" />
												</div>
											</div>
										))}
									</SkeletonTheme>
								</div>
							) : searchResults.length > 0 ? (
								<div className="search-results">
									<h6 className="mb-3">Search Results</h6>
									<div className="product-list">
										{searchResults.map((product) => (
											<div key={product.id} className="product-item" style={{
												display: 'flex',
												alignItems: 'center',
												padding: '15px',
												border: '1px solid #e9ecef',
												borderRadius: '8px',
												marginBottom: '10px',
												cursor: 'pointer',
												transition: 'background-color 0.2s'
											}}
												onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
												onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
												onClick={() => handleAddProduct(product)}
											>
												<img
													src={product.image}
													alt={product.name}
													style={{
														width: '60px',
														height: '60px',
														objectFit: 'cover',
														marginRight: '15px',
														borderRadius: '6px'
													}}
												/>
												<div className="product-info" style={{ flex: 1 }}>
													<div className="product-name" style={{
														fontWeight: '600',
														fontSize: '14px',
														marginBottom: '4px',
														color: '#333'
													}}>
														{product.name}
													</div>
													<div className="product-brand" style={{
														fontSize: '13px',
														color: '#666',
														marginBottom: '2px'
													}}>
														{product.brand}
													</div>
													<div className="product-price" style={{
														fontSize: '14px',
														fontWeight: '500',
														color: '#28a745'
													}}>
														${product.price}
													</div>
												</div>
											</div>
										))}
									</div>
								</div>
							) : searchQuery && !searchLoader ? (
								<div className="text-center py-3">
									<p className="text-muted">No products found for "{searchQuery}"</p>
								</div>
							) : null}

							{selectedProduct && (
								<div className="selected-product-card mb-4">
									<div className="d-flex justify-content-between align-items-center mb-3">
										<h6 className="mb-0">Selected Product</h6>
									</div>
									<div className="product-card">
										<div key={selectedProduct.id} className="product-card-content">
											<div className="product-card-image">
												<img
													src={selectedProduct.image}
													alt={selectedProduct.name}
												/>
											</div>
											<div className="product-card-details">
												<h6 className="product-card-name">{selectedProduct.name}</h6>
												<div className="product-card-brand" style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
													{selectedProduct.brand}
												</div>
												<div className="product-card-price">${selectedProduct.price}</div>
											</div>
											<button
												className="remove-icon-btn"
												onClick={() => removeProduct()}
												title="Remove product"
											>
												<i className="fa fa-times"></i>
											</button>
										</div>
									</div>
								</div>
							)}

							{!selectedProduct && (
								<div className="no-products-selected text-center py-4">
									<i className="fa fa-search fa-3x text-muted mb-3"></i>
									<p className="text-muted">Search and select a product above to add it to your bundle</p>
								</div>
							)}
						</div>
					</ModalBody>
					<ModalFooter>
						<Button className="cancel-btn cta autoWidth outline" outline onClick={() => toggleModal()}>
							Cancel
						</Button>
						<Button
							className="create-bundle-btn cta autoWidth"
							onClick={() => saveBundle()}
							disabled={!selectedProduct || bundleCreationLoader}
						>
							{bundleCreationLoader ? (
								<>
									<Spinner size="sm" />
									<span style={{ marginLeft: '8px' }}>Creating Bundle...</span>
								</>
							) : (
								'Create Bundle'
							)}
						</Button>
					</ModalFooter>
				</Modal>

				{/* Product Detail Page */}
				{showDetailPage && editProduct && (
					<div className="product-detail-overlay">
						<div className="product-detail-container">
							<div className="product-detail-header">
								<h3>Bundle Products</h3>
								<Button
									className="close-detail-btn"
									onClick={closeDetailPage}
								>
									<i className="fa fa-times"></i>
								</Button>
							</div>

							<div className="product-detail-content">
								<div className="bundle-products-horizontal">
									<div className="bundle-product-card main-product">
										<div className="product-image-container">
											<img
												src={editProduct.image}
												alt={editProduct.name}
											/>
										</div>
										<div className="product-info">
											<h5>{editProduct.name}</h5>
											<p className="price">${editProduct.price}</p>
										</div>
									</div>
									{bundleProducts.map((product, index) => (
										<>
											<div className="plus-icon">
												<i className="fa fa-plus"></i>
											</div>
											<div className="removed-product-placeholder">
												<div className="placeholder-content">
												
														{(product && product.id ) ?
															<div key={product.id} className="bundle-product-card additional-product">
																<div className="product-image-container">
																	<img
																		src={product.image}
																		alt={product.name}
																	/>
																	<button
																		className="remove-product-btn"
																		onClick={() => removeProductFromBundle(index)}
																		title="Remove from bundle"
																	>
																		<i className="fa fa-times"></i>
																	</button>
																</div>
																<div className="product-info">
																	<h5>{product.name}</h5>
																	<p className="price">${product.price}</p>
																</div>
															</div>
														:
															<>
																<button
																	className="add-product-placeholder-btn"
																	onClick={() => openAddProductModal(index)}
																	title="Add first product to bundle"
																>
																	<i className="fa fa-plus"></i>
																</button>
																<p>Add Product</p>
															</>
														}
												</div>
											</div>
										</>
									))}
								</div>
							</div>
							<div className="product-detail-footer">
								<Button
									className="cta autoWidth outline"
									onClick={closeDetailPage}
								>
									Cancel
								</Button>
								<Button
									className="cta autoWidth"
									onClick={saveBundleChanges}
									disabled={saveBundleLoader || getCurrentActiveProductCount() !== 2}
								>
									{saveBundleLoader ? (
										<>
											<Spinner size="sm" />
											<span style={{ marginLeft: '8px' }}>Saving...</span>
										</>
									) : (
										'Save'
									)}
								</Button>
							</div>
						</div>
					</div>
				)}

				{/* Add Product Back Modal */}
				<Modal isOpen={productBundleAddModal} toggle={closeAddProductModal} size="md">
					<ModalHeader toggle={closeAddProductModal}>
						<h4>Add More Products to Bundle</h4>
					</ModalHeader>
					<ModalBody>
					<div className="modal-content">
							<div className="search-row mb-4">
								<div className="search-container">
									<input
										type="text"
										className="form-control"
										placeholder="Search for products..."
										value={productSearchQuery}
										onChange={(e) => setProductSearchQuery(e.target.value)}
									/>
								</div>
							</div>

							{productSearchLoader ? (
								<div className="search-skeleton">
									<h6 className="mb-3">Search Results</h6>
									<SkeletonTheme baseColor="#f7fafc" highlightColor="#eff1ff">
										{[1, 2, 3].map((item) => (
											<div key={item} className="product-item-skeleton" style={{
												display: 'flex',
												alignItems: 'center',
												padding: '15px',
												border: '1px solid #e9ecef',
												borderRadius: '8px',
												marginBottom: '10px'
											}}>
												<Skeleton width={60} height={60} style={{ borderRadius: '6px', marginRight: '15px' }} />
												<div className="product-info" style={{ flex: 1 }}>
													<Skeleton height={16} width="70%" style={{ marginBottom: '4px' }} />
													<Skeleton height={14} width="50%" style={{ marginBottom: '2px' }} />
													<Skeleton height={14} width="30%" />
												</div>
											</div>
										))}
									</SkeletonTheme>
								</div>
							) : productSearchResult.length > 0 ? (
								<div className="search-results">
									<h6 className="mb-3">Search Results</h6>
									<div className="product-list">
										{productSearchResult.map((product) => (
											<div key={product.id} className="product-item" style={{
												display: 'flex',
												alignItems: 'center',
												padding: '15px',
												border: '1px solid #e9ecef',
												borderRadius: '8px',
												marginBottom: '10px',
												cursor: 'pointer',
												transition: 'background-color 0.2s'
											}}
												onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
												onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
												onClick={() => handleBundleProductSelect(product)}
											>
												<img
													src={product.image}
													alt={product.name}
													style={{
														width: '60px',
														height: '60px',
														objectFit: 'cover',
														marginRight: '15px',
														borderRadius: '6px'
													}}
												/>
												<div className="product-info" style={{ flex: 1 }}>
													<div className="product-name" style={{
														fontWeight: '600',
														fontSize: '14px',
														marginBottom: '4px',
														color: '#333'
													}}>
														{product.name}
													</div>
													<div className="product-brand" style={{
														fontSize: '13px',
														color: '#666',
														marginBottom: '2px'
													}}>
														{product.brand}
													</div>
													<div className="product-price" style={{
														fontSize: '14px',
														fontWeight: '500',
														color: '#28a745'
													}}>
														${product.price}
													</div>
												</div>
											</div>
										))}
									</div>
								</div>
							) : productSearchQuery && !productSearchLoader ? (
								<div className="text-center py-3">
									<p className="text-muted">No products found for "{productSearchQuery}"</p>
								</div>
							) : null}

							{productBundleSelect && (
								<div className="selected-product-card mb-4">
									<div className="d-flex justify-content-between align-items-center mb-3">
										<h6 className="mb-0">Selected Product</h6>
									</div>
									<div className="product-card">
										<div key={productBundleSelect.id} className="product-card-content">
											<div className="product-card-image">
												<img
													src={productBundleSelect.image}
													alt={productBundleSelect.name}
												/>
											</div>
											<div className="product-card-details">
												<h6 className="product-card-name">{productBundleSelect.name}</h6>
												<div className="product-card-brand" style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
													{productBundleSelect.brand}
												</div>
												<div className="product-card-price">${productBundleSelect.price}</div>
											</div>
											<button
												className="remove-icon-btn"
												onClick={() => removeProductSelectBundle()}
												title="Remove product"
											>
												<i className="fa fa-times"></i>
											</button>
										</div>
									</div>
								</div>
							)}

							{!productBundleSelect && (
								<div className="no-products-selected text-center py-4">
									<i className="fa fa-search fa-3x text-muted mb-3"></i>
									<p className="text-muted">Search and select a product above to add it to your bundle</p>
								</div>
							)}
						</div>
					</ModalBody>
					<ModalFooter>
						<Button className="cta autoWidth outline" onClick={closeAddProductModal}>
							Cancel
						</Button>
						<Button
							className="cta autoWidth"
							onClick={addProductToBundle}
							disabled={!productBundleSelect}
						>
							Add to Bundle
						</Button>
					</ModalFooter>
				</Modal>

				{/* Full Page Filter Slider */}
				{showFilterSlider && (
					<div className="full-page-filter-overlay">
						<div className="full-page-filter-slider">
							<div className="filter-header">
								<h3>Filter</h3>
								<Button
									className="close-filter-btn"
									onClick={() => setShowFilterSlider(false)}
								>
									<i className="fa fa-times"></i>
								</Button>
							</div>
							
							<div className="filter-content">
								{/* Search Section */}
								<div className="filter-section">
									<h4>Search</h4>
									<div className="search-input-container">
										<input
											type="text"
											className="form-control"
											placeholder="Search by product name or SKU"
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.target.value)}
										/>
									</div>
								</div>

								{/* Category Section */}
								<div className="filter-section">
									<h4>Categories</h4>
									<div className="category-select-container">
										<AsyncSelect
											isMulti
											isClearable
											cacheOptions
											value={selectedCategories}
											onChange={setSelectedCategories}
											defaultOptions
											loadOptions={debouncedCategoryQuery}
											placeholder="Select categories..."
											className="react-select-container"
											classNamePrefix="react-select"
										/>
									</div>
								</div>
							</div>

							<div className="filter-footer">
								<Button
									className="btn-outline-secondary me-2"
									onClick={async () => {
										setSearchQuery('');
										setSelectedCategories([]);
										setIsFiltered(false);
										setPage(1); // Reset to first page when clearing filters
										setShowFilterSlider(false);
										// Hit the API with no filters
										await getBundleProducts();
									}}
								>
									Clear All
								</Button>
								<Button
									className="btn-primary"
									onClick={async () => {
										setIsFiltered(searchQuery.trim() || selectedCategories.length > 0);
										setPage(1); // Reset to first page when applying filters
										setShowFilterSlider(false);
										setFilterLoader(true);
										await getBundleProducts();
										setFilterLoader(false);
									}}
									disabled={filterLoader}
								>
									{filterLoader ? <Spinner size="sm" /> : "Apply Filters"}
								</Button>
							</div>
						</div>
					</div>
				)}
			</div>
		</React.Fragment>
	);
}
export default DynamicBundling;
